import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DownloadOutlined,
  ExpandLess,
  ExpandMore,
  PowerSettingsNew,
  Refresh,
  WarningAmber,
} from '@mui/icons-material';
import { buildDownloadUrl, fetchData } from '../../../helpers/apiUtils';

/**
 * Reiter „Anzeigen" — Broker-Anzeigen LIVE auf finanztrends.de (Spec 2026-10-20 § 4).
 *
 * Not-Aus, Gewichte (Broker je Anlageklasse, Format je Gerät), Anzeigen je Broker × Format
 * (an/aus, gültig von/bis, Tracking-Link, Texte), Statistik mit CSV, Änderungsprotokoll.
 * Verwalten darf die ganze Broker-Hub-Gruppe; der Server prüft jede Eingabe (Compliance-Regeln,
 * Aktien/ETF nur Echtaktien-Anbieter). Pflichttexte und Verlustquoten kommen aus den Hub-
 * Compliance-Daten und sind hier nur zu sehen.
 */

const API = '/api/broker-hub/ads/live';

type Branch = 'aktie' | 'cfd' | 'krypto';
type Format = 'p1' | 'p3' | 'p4' | 'p5' | 'p6' | 'nl';
const KINDS = ['aktie', 'etf', 'index', 'rohstoff', 'krypto'] as const;
type Kind = (typeof KINDS)[number];
const KIND_LABEL: Record<Kind, string> = {
  aktie: 'Aktien',
  etf: 'ETF',
  index: 'Indizes',
  rohstoff: 'Rohstoffe',
  krypto: 'Krypto',
};
const FORMAT_LABEL: Record<Format, string> = {
  p1: 'P1 Sidebar oben',
  p3: 'P3 Broker-Check (Artikelende)',
  p4: 'P4 Leiste Artikelkopf',
  p5: 'P5 Sidebar-Mitte / Daumenleiste',
  p6: 'P6 Vergleichs-Teaser',
  nl: 'NL Newsletter',
};
const DEVICE_FORMATS: Record<'desktop' | 'mobile', Format[]> = {
  desktop: ['p1', 'p3', 'p4', 'p5', 'p6'],
  mobile: ['p1', 'p3', 'p4', 'p5', 'p6'],
};
const BRANCH_LABEL: Record<Branch, string> = {
  aktie: 'Aktien-Zweig',
  cfd: 'CFD-Zweig',
  krypto: 'Krypto-Zweig',
};
const GAP_LABEL: Record<string, string> = {
  verlustquote_fehlt: 'Verlustquote fehlt',
  verlustquote_spanne: 'Verlustquote als Spanne',
  verlustquote_widerspruch: 'Verlustquote widersprüchlich (Master ≠ FT)',
  ft_link_fehlt: 'FT-Affiliate-Link fehlt',
  eu_retail_nein: 'keine EU-Zulassung',
  nicht_kompilierbar: 'nicht auslieferbar',
};

interface Settings {
  enabled: boolean;
  pages: { article: boolean; snapshot: boolean };
  classWeights: Record<Kind, Record<string, number>>;
  formatWeights: {
    desktop: Partial<Record<Format, number>>;
    mobile: Partial<Record<Format, number>>;
  };
  holdout: { enabled: boolean; shareB: number };
}
interface BranchTexts {
  headline?: string;
  bullets?: string[];
  cta?: string;
}
interface Creative {
  id: number;
  brokerId: number;
  slug: string;
  format: Format;
  active: boolean;
  validFrom: string | null;
  validTo: string | null;
  trackingUrl: string | null;
  texts: Partial<Record<Branch, BranchTexts>> | null;
  version: number;
  updatedAt: string;
  updatedBy: string;
  defaults: Partial<Record<Branch, { headline: string; bullets: string[]; cta: string }>>;
}
interface BrokerKind {
  kind: Kind;
  allowed: boolean;
  branch?: Branch | null;
  riskKind?: string | null;
  quote?: string | null;
  quoteSource?: 'hub' | 'override' | null;
  gap?: string[];
}
interface BrokerStatus {
  slug: string;
  name: string;
  logo: string | null;
  hubStatus: string;
  profileLink: boolean;
  riskOverride: { percent: number; source: string; asOf: string } | null;
  kinds: BrokerKind[];
}
interface LogRow {
  id: number;
  at: string;
  user_email: string;
  action: string;
  target: string;
}
interface State {
  settings: Settings;
  settingsErrors: string[];
  creatives: Creative[];
  brokers: BrokerStatus[];
  log: LogRow[];
  today: string;
}
interface StatRow {
  day?: string;
  broker?: number;
  broker_name?: string;
  format?: string;
  asset_kind?: string;
  device?: string;
  page_type?: string;
  ad_version?: number;
  delivered: number;
  served: number;
  served_bot: number;
  viewable: number;
  viewable_consented: number;
  clicks_clean: number;
  clicks_flagged: number;
  clicks_intern: number;
  ctr: number | null;
  view_rate: number | null;
}

const KOPF = { backgroundColor: '#e0e0e0', color: 'black', fontWeight: 'bold' } as const;

function heuteBerlin(offsetTage = 0): string {
  const d = new Date(Date.now() - offsetTage * 86_400_000);
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

function anteil(w: Record<string, number | undefined>, k: string): string {
  const sum = Object.values(w).reduce<number>((s, v) => s + (v ?? 0), 0);
  const v = w[k] ?? 0;
  return sum > 0 && v > 0 ? `${Math.round((v / sum) * 100)} %` : '–';
}

// ── Not-Aus ─────────────────────────────────────────────────
const NotAus: React.FC<{ enabled: boolean; onChange: () => void }> = ({ enabled, onChange }) => {
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const schalten = async () => {
    setLaeuft(true);
    setFehler(null);
    const r = await fetchData<{ enabled: boolean }>(`${API}/kill`, {
      method: 'POST',
      body: JSON.stringify({ enabled: !enabled }),
    });
    setLaeuft(false);
    if (!r.success) {
      setFehler(r.error ?? 'Fehler');
      return;
    }
    setOffen(false);
    onChange();
  };
  return (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: enabled ? '#e8f5e9' : '#fdecea' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <PowerSettingsNew sx={{ color: enabled ? '#2e7d32' : '#c62828', fontSize: 36 }} />
        <Box flexGrow={1}>
          <Typography variant="h5" fontWeight="bold" color="black">
            {enabled ? 'Anzeigen laufen auf finanztrends.de' : 'Not-Aus aktiv — keine Anzeigen'}
          </Typography>
          <Typography variant="body2" color="black">
            {enabled
              ? 'Ein Klick auf „Not-Aus" stoppt alle Live-Anzeigen innerhalb von höchstens 30 Sekunden; Klicks auf schon angezeigte Anzeigen landen dann auf der FT-Startseite.'
              : 'Erst freischalten, wenn Gewichte, Anzeigen und Broker-Status unten stimmen.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color={enabled ? 'error' : 'success'}
          onClick={() => setOffen(true)}
        >
          {enabled ? 'Not-Aus' : 'Anzeigen freischalten'}
        </Button>
      </Stack>
      <Dialog open={offen} onClose={() => setOffen(false)}>
        <DialogTitle>
          {enabled ? 'Alle Live-Anzeigen stoppen?' : 'Live-Anzeigen freischalten?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {enabled
              ? 'Die Auslieferung endet für alle Leser innerhalb von höchstens 30 Sekunden.'
              : 'Ab sofort sehen alle Leser von finanztrends.de die Anzeigen nach den eingestellten Gewichten.'}
          </Typography>
          {fehler && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {fehler}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOffen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color={enabled ? 'error' : 'success'}
            disabled={laeuft}
            onClick={schalten}
          >
            {enabled ? 'Stoppen' : 'Freischalten'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ── Broker-Status ───────────────────────────────────────────
const BrokerStatusTable: React.FC<{ brokers: BrokerStatus[] }> = ({ brokers }) => (
  <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mb: 3 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={KOPF}>Broker</TableCell>
          {KINDS.map((k) => (
            <TableCell key={k} sx={KOPF}>
              {KIND_LABEL[k]}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {brokers.map((b) => (
          <TableRow key={b.slug}>
            <TableCell sx={{ color: 'black' }}>
              <Typography fontWeight="bold" color="black">
                {b.name}
              </Typography>
              {!b.profileLink && <Chip size="small" color="error" label="kein FT-Link" />}
              {b.hubStatus !== 'active' && (
                <Chip size="small" color="error" label={`Hub: ${b.hubStatus}`} />
              )}
            </TableCell>
            {b.kinds.map((k) => (
              <TableCell key={k.kind} sx={{ color: 'black', verticalAlign: 'top' }}>
                {!k.allowed ? (
                  <Typography variant="caption" color="#888">
                    nicht angeboten
                  </Typography>
                ) : k.gap && k.gap.length ? (
                  <Tooltip title="Fail-closed: so lange die Pflichtangabe fehlt, wird dieser Broker in der Klasse nicht ausgeliefert.">
                    <Chip
                      size="small"
                      color="error"
                      icon={<WarningAmber />}
                      label={k.gap.map((g) => GAP_LABEL[g] ?? g).join(', ')}
                    />
                  </Tooltip>
                ) : (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="black">
                      {k.branch ? BRANCH_LABEL[k.branch] : '–'}
                    </Typography>
                    {k.riskKind === 'cfd' ? (
                      <Tooltip
                        title={
                          k.quoteSource === 'override' && b.riskOverride
                            ? `Rückfall aus dem Katalog (${b.riskOverride.source}, Stand ${b.riskOverride.asOf}) — Hub-Compliance nachziehen.`
                            : 'Aus den Hub-Compliance-Daten (Master/FT).'
                        }
                      >
                        <Chip
                          size="small"
                          color={k.quoteSource === 'override' ? 'warning' : 'success'}
                          label={`Quote ${k.quote} %${k.quoteSource === 'override' ? ' (Katalog-Rückfall)' : ''}`}
                        />
                      </Tooltip>
                    ) : (
                      <Chip size="small" variant="outlined" label="Kapital-Hinweis" />
                    )}
                  </Stack>
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// ── Gewichte ────────────────────────────────────────────────
const Gewichte: React.FC<{ settings: Settings; brokers: BrokerStatus[]; onSaved: () => void }> = ({
  settings,
  brokers,
  onSaved,
}) => {
  const [form, setForm] = useState<Settings>(settings);
  const [fehler, setFehler] = useState<string[] | null>(null);
  const [ok, setOk] = useState(false);
  useEffect(() => {
    setForm(settings);
  }, [settings]);
  const dirty = JSON.stringify(form) !== JSON.stringify(settings);
  const slugs = brokers.map((b) => b.slug);
  const erlaubt = (slug: string, kind: Kind) =>
    brokers.find((b) => b.slug === slug)?.kinds.find((k) => k.kind === kind)?.allowed;
  const setW = (kind: Kind, slug: string, v: string) => {
    const n = Math.max(0, Math.min(1000, Math.floor(Number(v) || 0)));
    setForm((f) => {
      const cw = { ...f.classWeights[kind] };
      if (n > 0) cw[slug] = n;
      else delete cw[slug];
      return { ...f, classWeights: { ...f.classWeights, [kind]: cw } };
    });
  };
  const setF = (device: 'desktop' | 'mobile', format: Format, v: string) => {
    const n = Math.max(0, Math.min(1000, Math.floor(Number(v) || 0)));
    setForm((f) => ({
      ...f,
      formatWeights: { ...f.formatWeights, [device]: { ...f.formatWeights[device], [format]: n } },
    }));
  };
  const speichern = async () => {
    setFehler(null);
    setOk(false);
    const r = await fetchData<Settings>(`${API}/settings`, {
      method: 'PUT',
      body: JSON.stringify(form),
    });
    if (!r.success) {
      setFehler([r.error ?? 'Fehler', ...((r as unknown as { details?: string[] }).details ?? [])]);
      return;
    }
    setOk(true);
    onSaved();
  };
  return (
    <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
      <Typography variant="h6" color="black" gutterBottom>
        Gewichte (wirken ohne Deploy, ≤ 30 s)
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
        <Box>
          <Typography variant="subtitle2" color="black">
            Broker je Anlageklasse
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={KOPF}>Klasse</TableCell>
                {slugs.map((s) => (
                  <TableCell key={s} sx={KOPF}>
                    {brokers.find((b) => b.slug === s)?.name ?? s}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {KINDS.map((k) => (
                <TableRow key={k}>
                  <TableCell sx={{ color: 'black' }}>{KIND_LABEL[k]}</TableCell>
                  {slugs.map((s) => (
                    <TableCell key={s}>
                      {erlaubt(s, k) ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            type="number"
                            value={form.classWeights[k]?.[s] ?? 0}
                            onChange={(e) => setW(k, s, e.target.value)}
                            inputProps={{ min: 0, max: 1000, style: { width: 64, color: 'black' } }}
                          />
                          <Typography variant="caption" color="black">
                            {anteil(form.classWeights[k] ?? {}, s)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="#888">
                          {k === 'aktie' || k === 'etf' ? 'nur Echtaktien' : '–'}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="black">
            Format je Gerät (je Seitenaufruf genau eins)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={KOPF}>Format</TableCell>
                <TableCell sx={KOPF}>Desktop</TableCell>
                <TableCell sx={KOPF}>Mobil</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(['p1', 'p3', 'p4', 'p5', 'p6'] as Format[]).map((f) => (
                <TableRow key={f}>
                  <TableCell sx={{ color: 'black' }}>{FORMAT_LABEL[f]}</TableCell>
                  {(['desktop', 'mobile'] as const).map((d) => (
                    <TableCell key={d}>
                      {DEVICE_FORMATS[d].includes(f) ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            type="number"
                            value={form.formatWeights[d][f] ?? 0}
                            onChange={(e) => setF(d, f, e.target.value)}
                            inputProps={{ min: 0, max: 1000, style: { width: 64, color: 'black' } }}
                          />
                          <Typography variant="caption" color="black">
                            {anteil(form.formatWeights[d], f)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="#888">
                          –
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row" spacing={2} mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.pages.article}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, pages: { ...f.pages, article: e.target.checked } }))
                  }
                />
              }
              label={<Typography color="black">Artikel</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.pages.snapshot}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, pages: { ...f.pages, snapshot: e.target.checked } }))
                  }
                />
              }
              label={<Typography color="black">Snapshotseiten</Typography>}
            />
          </Stack>
          <Typography variant="subtitle2" color="black" mt={2}>
            Kontrollgruppe (Holdout-Test)
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={form.holdout.enabled}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, holdout: { ...f.holdout, enabled: e.target.checked } }))
                  }
                />
              }
              label={<Typography color="black">aktiv</Typography>}
            />
            <TextField
              size="small"
              type="number"
              label="Anteil Gruppe B (%)"
              value={form.holdout.shareB}
              onChange={(e) => {
                const n = Math.max(1, Math.min(100, Math.floor(Number(e.target.value) || 1)));
                setForm((f) => ({ ...f, holdout: { ...f.holdout, shareB: n } }));
              }}
              inputProps={{ min: 1, max: 100, style: { width: 64, color: 'black' } }}
            />
          </Stack>
          <Typography variant="caption" color="#555" display="block" sx={{ maxWidth: 420 }}>
            Aktiv: nur Leser mit Einwilligung kommen in den Test und werden fest Gruppe A (keine
            Broker-Anzeige) oder B (Anzeige) zugeteilt. Leser ohne Einwilligung sehen keine Anzeige.
          </Typography>
        </Box>
      </Stack>
      {fehler && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {fehler.map((f) => (
            <div key={f}>{f}</div>
          ))}
        </Alert>
      )}
      {ok && !dirty && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Gespeichert.
        </Alert>
      )}
      <Stack direction="row" spacing={2} mt={2}>
        <Button variant="contained" disabled={!dirty} onClick={speichern}>
          Gewichte speichern
        </Button>
        <Button disabled={!dirty} onClick={() => setForm(settings)}>
          Verwerfen
        </Button>
      </Stack>
    </Paper>
  );
};

// ── Anzeige (eine Zeile) ────────────────────────────────────
function zuLokal(v: string | null): string {
  return v ? v.slice(0, 16).replace(' ', 'T') : '';
}

const AnzeigeZeile: React.FC<{ c: Creative; name: string; onSaved: () => void }> = ({
  c,
  name,
  onSaved,
}) => {
  const [form, setForm] = useState(() => ({
    active: c.active,
    validFrom: zuLokal(c.validFrom),
    validTo: zuLokal(c.validTo),
    trackingUrl: c.trackingUrl ?? '',
    texts: (c.texts ?? {}) as Partial<Record<Branch, BranchTexts>>,
  }));
  const [auf, setAuf] = useState(false);
  const [fehler, setFehler] = useState<string[] | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => {
    setForm({
      active: c.active,
      validFrom: zuLokal(c.validFrom),
      validTo: zuLokal(c.validTo),
      trackingUrl: c.trackingUrl ?? '',
      texts: (c.texts ?? {}) as Partial<Record<Branch, BranchTexts>>,
    });
  }, [c]);
  const branches = Object.keys(c.defaults) as Branch[];
  const setText = (b: Branch, feld: 'headline' | 'cta', v: string) =>
    setForm((f) => {
      const t = { ...(f.texts[b] ?? {}) };
      if (v.trim()) t[feld] = v;
      else delete t[feld];
      return { ...f, texts: { ...f.texts, [b]: t } };
    });
  const setBullet = (b: Branch, i: number, v: string) =>
    setForm((f) => {
      const t = { ...(f.texts[b] ?? {}) };
      const bl = [...(t.bullets ?? c.defaults[b]?.bullets ?? ['', '', ''])];
      bl[i] = v;
      t.bullets = bl;
      return { ...f, texts: { ...f.texts, [b]: t } };
    });
  const zuruecksetzen = (b: Branch) =>
    setForm((f) => {
      const t = { ...f.texts };
      delete t[b];
      return { ...f, texts: t };
    });
  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    const texts: Partial<Record<Branch, BranchTexts>> = {};
    for (const [b, t] of Object.entries(form.texts) as Array<[Branch, BranchTexts]>) {
      if (t && Object.keys(t).length) texts[b] = t;
    }
    const r = await fetchData<Creative>(`${API}/creatives/${c.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        active: form.active,
        validFrom: form.validFrom || null,
        validTo: form.validTo || null,
        trackingUrl: form.trackingUrl.trim() || null,
        texts: Object.keys(texts).length ? texts : null,
      }),
    });
    setLaeuft(false);
    if (!r.success) {
      setFehler([r.error ?? 'Fehler', ...((r as unknown as { details?: string[] }).details ?? [])]);
      return;
    }
    onSaved();
  };
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ color: 'black' }}>{name}</TableCell>
        <TableCell sx={{ color: 'black' }}>{FORMAT_LABEL[c.format]}</TableCell>
        <TableCell>
          <Switch
            checked={form.active}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          />
        </TableCell>
        <TableCell>
          <Stack spacing={1}>
            <TextField
              size="small"
              type="datetime-local"
              label="von"
              InputLabelProps={{ shrink: true }}
              value={form.validFrom}
              onChange={(e) => setForm((f) => ({ ...f, validFrom: e.target.value }))}
            />
            <TextField
              size="small"
              type="datetime-local"
              label="bis"
              InputLabelProps={{ shrink: true }}
              value={form.validTo}
              onChange={(e) => setForm((f) => ({ ...f, validTo: e.target.value }))}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            fullWidth
            placeholder="leer = Affiliate-Link des FT-Profils"
            value={form.trackingUrl}
            onChange={(e) => setForm((f) => ({ ...f, trackingUrl: e.target.value }))}
            inputProps={{ style: { color: 'black' } }}
          />
        </TableCell>
        <TableCell sx={{ color: 'black' }}>
          <Tooltip title={`${c.updatedAt} · ${c.updatedBy}`}>
            <span>v{c.version}</span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => setAuf((x) => !x)} title="Texte">
              {auf ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Button size="small" variant="contained" disabled={laeuft} onClick={speichern}>
              Speichern
            </Button>
          </Stack>
        </TableCell>
      </TableRow>
      {fehler && (
        <TableRow>
          <TableCell colSpan={7}>
            <Alert severity="error">
              {fehler.map((f) => (
                <div key={f}>{f}</div>
              ))}
            </Alert>
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: auf ? undefined : 'none' }}>
          <Collapse in={auf} unmountOnExit>
            <Stack spacing={2} sx={{ py: 2 }}>
              <Typography variant="caption" color="black">
                Leer = Standardtext (grau). Platzhalter {'{ASSET}'} und {'{BROKER}'}.
                CFD-Überschrift muss „CFD" nennen; kein „kostenlos", kein Bonus im CFD-Zweig;
                Kostenaussage nur mit Marke ¹. Pflichttexte und Warnkasten sind nicht pflegbar.
              </Typography>
              {branches.map((b) => {
                const d = c.defaults[b];
                const t = form.texts[b] ?? {};
                return (
                  <Box key={b}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" color="black">
                        {BRANCH_LABEL[b]}
                      </Typography>
                      {form.texts[b] && (
                        <Button size="small" onClick={() => zuruecksetzen(b)}>
                          auf Standard
                        </Button>
                      )}
                    </Stack>
                    <Stack spacing={1} mt={1}>
                      <TextField
                        size="small"
                        label="Überschrift"
                        placeholder={d?.headline}
                        value={t.headline ?? ''}
                        onChange={(e) => setText(b, 'headline', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      {[0, 1, 2].map((i) => (
                        <TextField
                          key={i}
                          size="small"
                          label={`Vorteil ${i + 1}`}
                          placeholder={d?.bullets[i]}
                          value={t.bullets?.[i] ?? ''}
                          onChange={(e) => setBullet(b, i, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      ))}
                      <TextField
                        size="small"
                        label="Knopf"
                        placeholder={d?.cta}
                        value={t.cta ?? ''}
                        onChange={(e) => setText(b, 'cta', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Statistik ───────────────────────────────────────────────
const GRUPPEN = [
  { key: 'day', label: 'Tag' },
  { key: 'broker', label: 'Broker' },
  { key: 'format', label: 'Format' },
  { key: 'asset_kind', label: 'Klasse' },
  { key: 'device', label: 'Gerät' },
  { key: 'page_type', label: 'Seitentyp' },
  { key: 'ad_version', label: 'Version' },
];

// ── Kontrollgruppe ──────────────────────────────────────────
interface HoldoutGroupRow {
  grp: 'A' | 'B';
  eligible: number;
  leads: number;
  leadsPer1k: number | null;
  byDevice: Record<'desktop' | 'mobile' | 'unbekannt', { eligible: number; leads: number }>;
}
interface HoldoutData {
  groups: HoldoutGroupRow[];
  effect: { percent: number; ci95: [number, number] } | null;
  b: {
    served: number;
    viewable: number;
    clicks: number;
    ctr: number | null;
    ctrViewable: number | null;
  };
}

const zahl = (n: number) => n.toLocaleString('de-DE');
const prozent = (n: number | null) =>
  n === null ? '–' : `${n.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
const mitVorzeichen = (n: number) =>
  `${n > 0 ? '+' : ''}${n.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;

const HoldoutAuswertung: React.FC = () => {
  const [from, setFrom] = useState(() => heuteBerlin(6));
  const [to, setTo] = useState(() => heuteBerlin(0));
  const [data, setData] = useState<HoldoutData | null>(null);
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const holen = useCallback(async () => {
    setLaden(true);
    setFehler(null);
    const r = await fetchData<HoldoutData>(`${API}/holdout?from=${from}&to=${to}`);
    setLaden(false);
    if (!r.success || !r.data) {
      setFehler(r.error ?? 'Fehler');
      setData(null);
      return;
    }
    setData(r.data);
  }, [from, to]);
  useEffect(() => {
    void holen();
  }, [holen]);
  return (
    <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={1} flexWrap="wrap">
        <Typography variant="h6" color="black">
          Kontrollgruppe: A (ohne Broker-Anzeige) gegen B (mit)
        </Typography>
        <TextField
          size="small"
          type="date"
          label="von"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ style: { color: 'black' } }}
        />
        <TextField
          size="small"
          type="date"
          label="bis"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ style: { color: 'black' } }}
        />
        {laden && <CircularProgress size={18} />}
      </Stack>
      {fehler && <Alert severity="error">{fehler}</Alert>}
      {data && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  'Gruppe',
                  'berechtigte Aufrufe',
                  'Leads (DOI)',
                  'Leads je 1.000 Aufrufe',
                  'Desktop: Aufrufe / Leads',
                  'Mobil: Aufrufe / Leads',
                  'Leads ohne Gerät',
                ].map((h) => (
                  <TableCell key={h} sx={KOPF}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.groups.map((g) => (
                <TableRow key={g.grp}>
                  <TableCell sx={{ color: 'black' }}>
                    {g.grp === 'A' ? 'A – ohne Anzeige' : 'B – mit Anzeige'}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>{zahl(g.eligible)}</TableCell>
                  <TableCell sx={{ color: 'black' }}>{zahl(g.leads)}</TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    {g.leadsPer1k === null
                      ? '–'
                      : g.leadsPer1k.toLocaleString('de-DE', { maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    {zahl(g.byDevice.desktop.eligible)} / {zahl(g.byDevice.desktop.leads)}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    {zahl(g.byDevice.mobile.eligible)} / {zahl(g.byDevice.mobile.leads)}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>{zahl(g.byDevice.unbekannt.leads)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography color="black" mt={1.5}>
            {data.effect
              ? `Lead-Rate B gegenüber A: ${mitVorzeichen(data.effect.percent)} (95-%-Intervall ${mitVorzeichen(data.effect.ci95[0])} bis ${mitVorzeichen(data.effect.ci95[1])})`
              : 'Lead-Effekt: noch keine Leads in beiden Gruppen.'}
          </Typography>
          <Typography color="black">
            Broker-Anzeige in B: {zahl(data.b.served)} ausgespielt, {zahl(data.b.viewable)}{' '}
            sichtbar, {zahl(data.b.clicks)} Klicks · CTR {prozent(data.b.ctr)} (auf sichtbare{' '}
            {prozent(data.b.ctrViewable)})
          </Typography>
          <Typography variant="caption" color="#555" display="block" mt={1}>
            Aufrufe: Seitenaufrufe, auf denen eine Anzeige gezogen wurde — B bekam sie, A nicht.
            Leads: Einzelanalyse- und Leadwall-DOIs mit Gruppen-Marke in der adref. Liegt das ganze
            Intervall unter 0, kostet die Anzeige nachweisbar Leads; schließt es 0 ein, reicht die
            Datenmenge noch nicht für eine Aussage. XTB gegen Tickmill: Statistik unten nach Broker
            und Klasse gruppieren.
          </Typography>
        </>
      )}
    </Paper>
  );
};

const Statistik: React.FC = () => {
  const [from, setFrom] = useState(() => heuteBerlin(13));
  const [to, setTo] = useState(() => heuteBerlin(0));
  const [group, setGroup] = useState<string[]>(['broker', 'format']);
  const [rows, setRows] = useState<StatRow[]>([]);
  const [top, setTop] = useState<
    Array<{ key: string; served: number; viewable: number; clicks: number }>
  >([]);
  const [by, setBy] = useState<'post' | 'isin'>('post');
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const groupStr = group.join(',');
  const laden1 = useCallback(async () => {
    setLaden(true);
    setFehler(null);
    const [s, t] = await Promise.all([
      fetchData<StatRow[]>(`${API}/stats?from=${from}&to=${to}&group=${groupStr}`),
      fetchData<Array<{ key: string; served: number; viewable: number; clicks: number }>>(
        `${API}/top?from=${from}&to=${to}&by=${by}`,
      ),
    ]);
    setLaden(false);
    if (!s.success) setFehler(s.error ?? 'Fehler');
    setRows(s.data ?? []);
    setTop(t.data ?? []);
  }, [from, to, groupStr, by]);
  useEffect(() => {
    void laden1();
  }, [laden1]);
  const summe = useMemo(
    () =>
      rows.reduce(
        (s, r) => ({
          delivered: s.delivered + r.delivered,
          served: s.served + r.served,
          viewable: s.viewable + r.viewable,
          clicks: s.clicks + r.clicks_clean,
        }),
        { delivered: 0, served: 0, viewable: 0, clicks: 0 },
      ),
    [rows],
  );
  const aktualisieren = async () => {
    await fetchData(`${API}/refresh`, { method: 'POST' });
    await laden1();
  };
  const csvUrl = buildDownloadUrl(`${API}/stats`, { from, to, group: groupStr, format: 'csv' });
  return (
    <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h6" color="black">
          Statistik
        </Typography>
        <TextField
          size="small"
          type="date"
          label="von"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="bis"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <ToggleButtonGroup size="small" value={group} onChange={(_e, v: string[]) => setGroup(v)}>
          {GRUPPEN.map((g) => (
            <ToggleButton key={g.key} value={g.key}>
              {g.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button size="small" startIcon={<Refresh />} onClick={aktualisieren}>
          Heute neu rechnen
        </Button>
        <Button size="small" startIcon={<DownloadOutlined />} href={csvUrl}>
          CSV
        </Button>
        {laden && <CircularProgress size={18} />}
      </Stack>
      <Typography variant="caption" color="black" display="block" mt={1}>
        Ausgeliefert = Server-Antworten mit Anzeige (gewählter Broker). Gezeigt/sichtbar ohne Bots;
        sichtbar = 50 % der Anzeige 1 s im Bild. Klicks = Affiliate-Klicks ohne
        Verdachts-Markierung; CTR = Klicks / gezeigt. Tageswerte werden halbstündlich gerechnet.
      </Typography>
      {fehler && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {fehler}
        </Alert>
      )}
      <Stack direction="row" spacing={2} my={2}>
        <Chip label={`Ausgeliefert ${summe.delivered.toLocaleString('de-DE')}`} />
        <Chip label={`Gezeigt ${summe.served.toLocaleString('de-DE')}`} />
        <Chip label={`Sichtbar ${summe.viewable.toLocaleString('de-DE')}`} />
        <Chip label={`Klicks ${summe.clicks.toLocaleString('de-DE')}`} />
        <Chip
          label={`CTR ${summe.served ? ((summe.clicks / summe.served) * 100).toFixed(2) : '–'} %`}
        />
      </Stack>
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {group.map((g) => (
                <TableCell key={g} sx={{ ...KOPF, backgroundColor: '#e0e0e0 !important' }}>
                  {GRUPPEN.find((x) => x.key === g)?.label}
                </TableCell>
              ))}
              {[
                'Ausgeliefert',
                'Gezeigt',
                'Bots',
                'Sichtbar',
                'Sichtbar %',
                'Klicks',
                'markiert',
                'intern',
                'CTR %',
              ].map((h) => (
                <TableCell
                  key={h}
                  align="right"
                  sx={{ ...KOPF, backgroundColor: '#e0e0e0 !important' }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i} hover>
                {group.map((g) => (
                  <TableCell key={g} sx={{ color: 'black' }}>
                    {g === 'broker'
                      ? r.broker_name
                      : g === 'format'
                        ? (FORMAT_LABEL[r.format as Format] ?? r.format)
                        : String((r as unknown as Record<string, unknown>)[g] ?? '')}
                  </TableCell>
                ))}
                {[
                  r.delivered,
                  r.served,
                  r.served_bot,
                  r.viewable,
                  r.view_rate ?? '–',
                  r.clicks_clean,
                  r.clicks_flagged,
                  r.clicks_intern,
                  r.ctr ?? '–',
                ].map((v, j) => (
                  <TableCell key={j} align="right" sx={{ color: 'black' }}>
                    {typeof v === 'number' ? v.toLocaleString('de-DE') : v}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={group.length + 9} sx={{ color: 'black' }}>
                  Keine Daten im Zeitraum.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="subtitle1" color="black">
          Top
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={by} onChange={(_e, v) => v && setBy(v)}>
          <ToggleButton value="post">Artikel (post_id)</ToggleButton>
          <ToggleButton value="isin">Assets (ISIN)</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="black">
          aus den Rohzeilen, höchstens 90 Tage zurück
        </Typography>
      </Stack>
      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={KOPF}>{by === 'post' ? 'Artikel' : 'ISIN'}</TableCell>
            <TableCell sx={KOPF} align="right">
              Gezeigt
            </TableCell>
            <TableCell sx={KOPF} align="right">
              Sichtbar
            </TableCell>
            <TableCell sx={KOPF} align="right">
              Klicks
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {top.map((t) => (
            <TableRow key={t.key}>
              <TableCell sx={{ color: 'black' }}>
                {by === 'post' ? (
                  <a
                    href={`https://www.finanztrends.de/?p=${t.key}`}
                    target="_blank"
                    rel="noopener"
                  >
                    {t.key}
                  </a>
                ) : (
                  t.key
                )}
              </TableCell>
              <TableCell align="right" sx={{ color: 'black' }}>
                {t.served.toLocaleString('de-DE')}
              </TableCell>
              <TableCell align="right" sx={{ color: 'black' }}>
                {t.viewable.toLocaleString('de-DE')}
              </TableCell>
              <TableCell align="right" sx={{ color: 'black' }}>
                {t.clicks.toLocaleString('de-DE')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

// ── Newsletter-Vorschau ─────────────────────────────────────
// Spec § 5: NL ist gebaut, aber nicht im Versand. Die Vorschau rendert die Mail-Anzeige mit einem
// absichtlich ungültigen Klick-Link (zählt nicht, ruft keinen Affiliate-Link auf).
const NlVorschau: React.FC<{ brokers: BrokerStatus[] }> = ({ brokers }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [titel, setTitel] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [breite, setBreite] = useState<number>(640);
  const zeigen = async (slug: string, branch: Branch, name: string) => {
    setFehler(null);
    const r = await fetchData<{ html: string }>(
      `${API}/nl-preview?broker=${slug}&branch=${branch}`,
    );
    if (!r.success || !r.data) {
      setFehler(r.error ?? 'Vorschau nicht verfügbar');
      return;
    }
    setTitel(`${name} · ${BRANCH_LABEL[branch]}`);
    setHtml(r.data.html);
  };
  const knoepfe: Array<{ slug: string; branch: Branch; name: string }> = [];
  for (const b of brokers) {
    const zweige = new Set(
      b.kinds.filter((k) => k.allowed && k.branch).map((k) => k.branch as Branch),
    );
    for (const z of zweige) knoepfe.push({ slug: b.slug, branch: z, name: b.name });
  }
  return (
    <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffffff' }}>
      <Typography variant="h6" color="black">
        Newsletter-Anzeige (Vorschau, nicht im Versand)
      </Typography>
      <Typography variant="caption" color="black" display="block" mb={1}>
        Platzierung im Newsletter ist offen (Christian/Yannick). Die Vorschau zeigt die Mail-Fassung
        mit vollem Warnhinweis; der Knopf zählt hier nicht.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {knoepfe.map((k) => (
          <Button
            key={k.slug + k.branch}
            size="small"
            variant="outlined"
            onClick={() => zeigen(k.slug, k.branch, k.name)}
          >
            {k.name} · {BRANCH_LABEL[k.branch]}
          </Button>
        ))}
      </Stack>
      {fehler && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {fehler}
        </Alert>
      )}
      <Dialog open={!!html} onClose={() => setHtml(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <span>Newsletter-Vorschau: {titel}</span>
            <Box flexGrow={1} />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={breite}
              onChange={(_e, v) => v && setBreite(v)}
            >
              <ToggleButton value={640}>Desktop</ToggleButton>
              <ToggleButton value={390}>Handy</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {html && (
            <iframe
              title="Newsletter-Vorschau"
              srcDoc={html}
              sandbox=""
              style={{
                width: breite,
                maxWidth: '100%',
                height: 820,
                border: '1px solid #ddd',
                display: 'block',
                margin: '0 auto',
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHtml(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ── Reiter ──────────────────────────────────────────────────
const AdsLiveTab: React.FC = () => {
  const [state, setState] = useState<State | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [logAuf, setLogAuf] = useState(false);
  const holen = useCallback(async () => {
    const r = await fetchData<State>(API);
    setLaden(false);
    if (!r.success || !r.data) {
      setFehler(r.error ?? 'Fehler beim Laden');
      return;
    }
    setFehler(null);
    setState(r.data);
  }, []);
  useEffect(() => {
    void holen();
  }, [holen]);
  if (laden) return <CircularProgress />;
  if (fehler || !state) return <Alert severity="error">{fehler}</Alert>;
  const nameOf = (slug: string) => state.brokers.find((b) => b.slug === slug)?.name ?? slug;
  return (
    <Box>
      <NotAus enabled={state.settings.enabled} onChange={holen} />
      {state.settingsErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Einstellungs-Dokument ungültig — es gilt der Not-Aus: {state.settingsErrors.join('; ')}
        </Alert>
      )}
      <Typography variant="h6" color="text.primary" gutterBottom>
        Broker-Status (was jetzt ausgeliefert würde)
      </Typography>
      <BrokerStatusTable brokers={state.brokers} />
      <Gewichte settings={state.settings} brokers={state.brokers} onSaved={holen} />
      <Typography variant="h6" color="text.primary" gutterBottom>
        Anzeigen je Broker und Format
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Broker', 'Format', 'aktiv', 'gültig', 'eigener Tracking-Link', 'Version', ''].map(
                (h) => (
                  <TableCell key={h} sx={KOPF}>
                    {h}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {state.creatives.map((c) => (
              <AnzeigeZeile key={c.id} c={c} name={nameOf(c.slug)} onSaved={holen} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <HoldoutAuswertung />
      <Statistik />
      <NlVorschau brokers={state.brokers} />
      <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" color="black">
            Änderungsprotokoll
          </Typography>
          <IconButton size="small" onClick={() => setLogAuf((x) => !x)}>
            {logAuf ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>
        <Collapse in={logAuf}>
          <Table size="small">
            <TableBody>
              {state.log.map((l) => (
                <TableRow key={l.id}>
                  <TableCell sx={{ color: 'black', whiteSpace: 'nowrap' }}>{l.at}</TableCell>
                  <TableCell sx={{ color: 'black' }}>{l.user_email}</TableCell>
                  <TableCell sx={{ color: 'black' }}>{l.action}</TableCell>
                  <TableCell sx={{ color: 'black' }}>{l.target}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default AdsLiveTab;
